import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getBackgroundColor, getBorderColor } from '../types/IPerformanceVersion2';

export interface PDFSection {
  id: string;
  title: string;
  element?: HTMLElement;
  needsApiCall?: boolean;
  apiFunction?: () => Promise<any>;
  renderFunction?: (data: any) => Promise<HTMLElement>;
}

export interface PDFExportOptions {
  filename?: string;
  format?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  quality?: number;
  scale?: number;
  backgroundColor?: string;
}

export class SimulationPDFExporter {
  private pdf: jsPDF;
  private options: Required<PDFExportOptions>;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 20;
  private currentY: number = 20;

  constructor(options: PDFExportOptions = {}) {
    this.options = {
      filename: options.filename || 'simulation-report',
      format: options.format || 'a4',
      orientation: options.orientation || 'portrait',
      quality: options.quality || 0.95,
      scale: options.scale || 2,
      backgroundColor: options.backgroundColor || '#ffffff'
    };

    this.pdf = new jsPDF({
      orientation: this.options.orientation,
      unit: 'mm',
      format: this.options.format
    });

    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
  }

  
  /**
   * Captures an HTML element as an image
   */
  private async captureElement(element: HTMLElement): Promise<string> {
    console.log('Capturing element:', element.className, element.getBoundingClientRect());

    try {
      // Check if element is already positioned off-screen for PDF
      let elementToCapture = element;

      if (element.style.position !== 'absolute' || element.style.left !== '-9999px') {
        // Create a clone to work with so we don't affect the original DOM
        const clonedElement = element.cloneNode(true) as HTMLElement;
        clonedElement.style.position = 'absolute';
        clonedElement.style.left = '-9999px';
        clonedElement.style.top = '0';
        clonedElement.style.width = `${element.offsetWidth}px`;
        clonedElement.style.height = `${element.offsetHeight}px`;
        document.body.appendChild(clonedElement);
        elementToCapture = clonedElement;

        // Small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Capture directly with html2canvas - no image conversion needed
      const canvas = await html2canvas(elementToCapture, {
        scale: Math.min(this.options.scale, 2),
        useCORS: true,
        allowTaint: true,
        backgroundColor: this.options.backgroundColor,
        width: elementToCapture.scrollWidth || elementToCapture.offsetWidth,
        height: elementToCapture.scrollHeight || elementToCapture.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        foreignObjectRendering: false,
        imageTimeout: 10000
      });

      // Clean up if we created a clone
      if (elementToCapture !== element && elementToCapture.parentNode) {
        document.body.removeChild(elementToCapture);
      }

      // Simple validation - check if canvas has content
      if (canvas.width > 0 && canvas.height > 0) {
        console.log('✅ Capture successful');
        return canvas.toDataURL('image/png', this.options.quality);
      }

      throw new Error('Canvas has invalid dimensions');
    } catch (error) {
      console.error('Capture failed:', error);
      throw error;
    }
  }

  
  /**
   * Adds a title to the PDF page
   */
  private addTitle(title: string, fontSize: number = 16): void {
    this.pdf.setFontSize(fontSize);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title, this.margin, this.currentY);
    this.currentY += 10;
  }

  /**
   * Adds an image to the PDF with proper scaling
   */
  private async addImage(imageDataUrl: string, title?: string, addNewPage: boolean = false): Promise<void> {
    const imgWidth = this.pageWidth - 2 * this.margin;

    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        try {
          const imgHeight = (img.height * imgWidth) / img.width;

          // Add a new page if requested or if image doesn't fit
          if (addNewPage || this.currentY + imgHeight > this.pageHeight - this.margin) {
            if (this.currentY > this.margin) { // Don't add page for first image
              this.pdf.addPage();
            }
            this.currentY = this.margin;
          }

          if (title) {
            this.addTitle(title, 12);
          }

          this.pdf.addImage(imageDataUrl, 'PNG', this.margin, this.currentY, imgWidth, imgHeight);
          this.currentY += imgHeight + 10;
        } catch (error) {
          console.error('Error adding image to PDF:', error);
          // Don't add any fallback text - just resolve
        }
        resolve();
      };

      img.onerror = () => {
        console.error('Failed to load image for PDF');
        // Don't add any fallback text - just resolve
        resolve();
      };

      img.src = imageDataUrl;
    });
  }

  /**
   * Waits for all API calls to complete
   */
  private async fetchAllSectionData(sections: PDFSection[]): Promise<Map<string, any>> {
    const dataMap = new Map<string, any>();

    const apiPromises = sections
      .filter(section => section.needsApiCall && section.apiFunction)
      .map(async section => {
        try {
          const data = await section.apiFunction!();
          dataMap.set(section.id, data);
        } catch (error) {
          console.error(`Failed to fetch data for section ${section.id}:`, error);
          dataMap.set(section.id, null);
        }
      });

    await Promise.all(apiPromises);
    return dataMap;
  }

  /**
   * Generates the complete PDF report
   */
  async generatePDF(sections: PDFSection[]): Promise<void> {
    try {
      // Fetch all API data first
      const dataMap = await this.fetchAllSectionData(sections);

      let isFirstSection = true;

      // Process each section - NO cover page
      for (const section of sections) {
        try {
          let elementToCapture: HTMLElement;

          if (section.element) {
            // Element exists in DOM, capture it directly
            elementToCapture = section.element;
          } else if (section.renderFunction) {
            // Create custom element
            const data = dataMap.get(section.id);
            elementToCapture = await section.renderFunction(data);
          } else {
            console.warn(`No element or render function for section: ${section.id}`);
            continue;
          }

          // Capture and add to PDF
          const imageDataUrl = await this.captureElement(elementToCapture);

          // Only add to PDF if capture was successful
          if (imageDataUrl && imageDataUrl.startsWith('data:image')) {
            // Add new page for each section after the first
            await this.addImage(imageDataUrl, undefined, !isFirstSection);
            isFirstSection = false;
          } else {
            console.error(`Invalid image data URL for section ${section.id}`);
            continue;
          }

          // Clean up temporary elements
          if (!section.element && elementToCapture.parentNode) {
            // Remove from DOM if it was temporarily added
            if (elementToCapture.style.position === 'absolute' && elementToCapture.style.left === '-9999px') {
              document.body.removeChild(elementToCapture);
            } else {
              elementToCapture.parentNode.removeChild(elementToCapture);
            }
          }
        } catch (sectionError) {
          console.error(`Error processing section ${section.id}:`, sectionError);
          // Continue with other sections instead of failing entirely
          continue;
        }
      }

      // Save the PDF
      this.pdf.save(`${this.options.filename}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  
  
  /**
   * Export simulation data to PDF -- create multiple pages as needed
   */
async exportSimulationReport(data: {
  }, options?: {
    includePitch?: boolean;
    includeStylisticFit?: boolean;
  }): Promise<void> {
    const sections: PDFSection[] = [];

    // First page: Capture the pitch
    if (options?.includePitch !== false) {
      const pitchContainer = document.querySelector('.pitch-container-for-pdf');

      if (pitchContainer) {
        const element = pitchContainer as HTMLElement;
        const rect = element.getBoundingClientRect();

        if (rect.width > 500 && rect.height > 300) {
          sections.push({
            id: 'pitch',
            title: 'Team Formation & Chemistry',
            element: element
          });
        }
        await this.generatePDF(sections);
      } else {
        console.warn('Pitch container with class "pitch-container-for-pdf" not found');
      }
    }
  }
}

/**
 * Convenience function to export simulation report
 */
export const exportSimulationToPDF = async (
  simulationData: any,
  options?: PDFExportOptions & {
    includePitch?: boolean;
    includeStylisticFit?: boolean;
  }
): Promise<void> => {
  const exporter = new SimulationPDFExporter(options);
  await exporter.exportSimulationReport(simulationData, options);
};